import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyContext } from '../../../../../../../context/CompanyContext';
import { ProductTransactionExportConfigurationContext } from '../../../../../../../context/ProductTransactionExportConfigurationContext';
import {
  ProductTransactionExportConfiguration,
  ProductTransactionExportConfigurationField,
  ProductTransactionExportConfigurationInput,
} from '../../../../../../../types/product_transaction_export_configuration';
import ProductTransactionExportConfigurationForm, {
  DraggableProductTransactionExportFieldIdentifier,
} from '../Common/ProductTransactionExportConfigurationForm';
import { Routing } from '../../../../../../../util/routes.util';
import { ISO_LanguageCode } from '../../../../../../../types/translation/localization.utils';
import { TranslationLanguage } from '../../../../../../../types/translation/translation';
import { useRequest } from '../../../../../../../requests/common/requestHook';
import { ProductTransactionExportConfigurationRequest } from '../../../../../../../requests/productTransactionExportConfiguration.request';
import { DraggableItemData } from '../../../../../Common/DraggableItem';
import { ButtonVariant } from '../../../../../../components/Button/Button';
import { BackButton, CreateButton } from '../../../../../../components/Button';

interface CreateProductTransactionExportConfigurationPaneProps {}

export default function CreateProductTransactionExportConfigurationPane({}: CreateProductTransactionExportConfigurationPaneProps) {
  const navigate = useNavigate();
  const { currentCompany } = useContext(CompanyContext);
  const {
    productTransactionExportConfigurations,
    setProductTransactionExportConfigurations,
    productTransactionExportConfigurationsLoading,
  } = useContext(ProductTransactionExportConfigurationContext);

  const [input, setInput] = useState<ProductTransactionExportConfigurationInput>(
    new ProductTransactionExportConfigurationInput(
      ProductTransactionExportConfiguration.default(
        currentCompany.id,
        (currentCompany.settings.language || ISO_LanguageCode.EN) as TranslationLanguage,
      ),
    ),
  );

  const [create, { loading }] = useRequest(ProductTransactionExportConfigurationRequest.create);

  const handleCreate = async (
    configuration: ProductTransactionExportConfigurationInput,
    items: DraggableProductTransactionExportFieldIdentifier[],
  ) => {
    configuration.mapping = DraggableItemData.toMap<ProductTransactionExportConfigurationField>(items);

    const result = await create({ data: [new ProductTransactionExportConfigurationInput(configuration).forCreate()] });
    if (!result) return;

    const config = result[0];
    productTransactionExportConfigurations.set(config.id, new ProductTransactionExportConfiguration(config));
    setProductTransactionExportConfigurations(new Map(productTransactionExportConfigurations));
    navigate(Routing.stock.history.settings.report.overview());
  };

  const footer = (
    configuration: ProductTransactionExportConfigurationInput,
    items: DraggableProductTransactionExportFieldIdentifier[],
  ) => {
    return (
      <div className='flex justify-end'>
        <div className='flex items-center gap-2'>
          <BackButton
            disabled={loading}
            variant={ButtonVariant.secondary}
            onClick={() => navigate(Routing.stock.products.settings.report.overview())}
          />
          <CreateButton
            loading={loading}
            onClick={() => handleCreate(configuration, items)}
            disabled={!configuration.filename || !configuration.name}
          />
        </div>
      </div>
    );
  };

  return (
    <ProductTransactionExportConfigurationForm
      input={input}
      setInput={setInput}
      footer={footer}
      loading={productTransactionExportConfigurationsLoading}
    />
  );
}
